import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import { Decoration, DecorationSet } from "prosemirror-view"

import type { Node as ProseMirrorNode } from "prosemirror-model"

export type AiEditHighlightRange = {
  from: number
  to: number
}

export type AiEditSuggestion = {
  id: string
  ranges: AiEditHighlightRange[]
}

export type AiEditHighlightState = Map<string, AiEditHighlightRange[]>

export type AiEditHighlightMeta =
  | { action: "add"; id: string; ranges: AiEditHighlightRange[] }
  | { action: "remove"; id: string }
  | { action: "clear" }

export const aiEditHighlightKey = new PluginKey<AiEditHighlightState>("aiEditHighlight")

function normalizeRange(
  range: AiEditHighlightRange,
  doc: ProseMirrorNode
): AiEditHighlightRange | null {
  const maxPos = doc.content.size
  const from = Math.max(0, Math.min(range.from, maxPos))
  const to = Math.max(0, Math.min(range.to, maxPos))

  if (from === to) return null
  return from < to ? { from, to } : { from: to, to: from }
}

export function getSuggestionAtPosition(
  state: AiEditHighlightState,
  position: number
): AiEditSuggestion | null {
  for (const [id, ranges] of state.entries()) {
    for (const range of ranges) {
      if (position >= range.from && position <= range.to) {
        return { id, ranges }
      }
    }
  }
  return null
}

export function rangesOverlap(rangeA: AiEditHighlightRange, rangeB: AiEditHighlightRange): boolean {
  return rangeA.from < rangeB.to && rangeB.from < rangeA.to
}

export function isSelectionOverlappingAnySuggestion(
  state: AiEditHighlightState,
  from: number,
  to: number
): boolean {
  const selectionRange = { from, to }
  for (const ranges of state.values()) {
    for (const range of ranges) {
      if (rangesOverlap(selectionRange, range)) {
        return true
      }
    }
  }
  return false
}

export const AiEditHighlight = Extension.create({
  name: "aiEditHighlight",
  addProseMirrorPlugins() {
    return [
      new Plugin<AiEditHighlightState>({
        key: aiEditHighlightKey,
        state: {
          init: () => new Map(),
          apply: (tr, value) => {
            const meta = tr.getMeta(aiEditHighlightKey) as AiEditHighlightMeta | undefined

            if (meta) {
              const newState = new Map(value)

              switch (meta.action) {
                case "add": {
                  const normalizedRanges = meta.ranges
                    .map((range) => normalizeRange(range, tr.doc))
                    .filter((range): range is AiEditHighlightRange => range !== null)
                  if (normalizedRanges.length > 0) {
                    newState.set(meta.id, normalizedRanges)
                  }
                  return newState
                }
                case "remove": {
                  newState.delete(meta.id)
                  return newState
                }
                case "clear": {
                  return new Map()
                }
              }
            }

            if (value.size === 0 || !tr.docChanged) return value

            const newState = new Map<string, AiEditHighlightRange[]>()
            for (const [id, ranges] of value.entries()) {
              const mappedRanges = ranges
                .map((range) => {
                  const mapped = { from: tr.mapping.map(range.from), to: tr.mapping.map(range.to) }
                  return normalizeRange(mapped, tr.doc)
                })
                .filter((range): range is AiEditHighlightRange => range !== null)
              if (mappedRanges.length > 0) {
                newState.set(id, mappedRanges)
              }
            }
            return newState
          },
        },
        props: {
          decorations: (editorState) => {
            const suggestions = aiEditHighlightKey.getState(editorState)
            if (!suggestions || suggestions.size === 0) return null

            const decorations: Decoration[] = []
            for (const [id, ranges] of suggestions.entries()) {
              for (const range of ranges) {
                const $from = editorState.doc.resolve(range.from)
                let cellDepth = -1
                for (let depth = $from.depth; depth > 0; depth--) {
                  const node = $from.node(depth)
                  if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
                    cellDepth = depth
                    break
                  }
                }
                if (cellDepth !== -1) {
                  decorations.push(
                    Decoration.node($from.before(cellDepth), $from.after(cellDepth), {
                      class: "ai-edit-highlight",
                      "data-ai-edit-id": id,
                    })
                  )
                } else {
                  decorations.push(
                    Decoration.inline(range.from, range.to, {
                      class: "ai-edit-highlight",
                      "data-ai-edit-id": id,
                    })
                  )
                }
              }
            }

            return DecorationSet.create(editorState.doc, decorations)
          },
        },
      }),
    ]
  },
})
